import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { PageHero, Tabs, Icon, IconTile, Badge, Button, Skeleton, EmptyState } from '../../components/ui';
import { fetchAllPujas, fetchPujaCategories } from '../../services/epujaService';

const PUJA_IMAGES = [
  '/images/design/hero-ram-mandir.webp',
  '/images/kanak-bhawan/temple.jpg',
  '/images/hanuman/temple-1.webp',
  '/images/ram-lala/ram-lala-01.jpg',
  '/images/design/ram-mandir-night.webp',
  '/images/ghats/saryu-ghat-1.jpeg',
];

const CATEGORY_LABELS = {
  all: 'All', puja: 'Pujas', havan: 'Havans', jaap: 'Jaaps', path: 'Paths',
  bhajan: 'Bhajans', festival: 'Festival', shanti: 'Shanti', pitru: 'Pitru',
};

const FEATURES = [
  { icon: 'badge-check', label: 'Verified pandit', desc: 'Temple-registered pandits' },
  { icon: 'video', label: 'Live video', desc: 'Watch live or as a recording' },
  { icon: 'package', label: 'Prasad delivery', desc: 'Blessed prasad shipped home' },
  { icon: 'award', label: 'Digital certificate', desc: 'Sankalp completion proof' },
];

export default function EPuja() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pujas, setPujas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const activeCategory = searchParams.get('category') || 'all';
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });

  const loadPujas = useCallback(async () => {
    try {
      setLoading(true); setError(false);
      const filters = { limit: 24 };
      if (activeCategory !== 'all') filters.category = activeCategory;
      if (search) filters.search = search;
      const data = await fetchAllPujas(filters);
      setPujas(data.data || []);
      setPagination(data.pagination || { total: (data.data || []).length, page: 1, totalPages: 1 });
    } catch {
      setError(true);
      setPujas([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, search]);

  useEffect(() => { fetchPujaCategories().then((c) => setCategories(Array.isArray(c) ? c : [])).catch(() => setCategories([])); }, []);
  useEffect(() => { loadPujas(); }, [loadPujas, reload]);

  const getCategoryCount = (cat) => categories.find((c) => c.category === cat)?.count || 0;

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') next.delete(key); else next.set(key, value);
    setSearchParams(next, { replace: true });
  };
  const onSearch = (v) => { setSearch(v); setParam('search', v); };

  const tabs = Object.entries(CATEGORY_LABELS).map(([id, label]) => ({
    id, label: t(label), count: id === 'all' ? pagination.total || pujas.length : getCategoryCount(id),
  })).filter((x) => x.id === 'all' || x.count > 0 || x.id === activeCategory);

  const price = (p) => Number(p.price ?? p.basePrice ?? p.packages?.[0]?.price ?? 0) || null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Puja & Seva Booking in Ayodhya',
    description: 'Sponsor a puja performed by a verified pandit at an Ayodhya temple, watch it live and receive blessed prasad at home.',
    url: 'https://ayodhyadham.info/epuja',
    provider: { '@id': 'https://ayodhyadham.info/#organization' },
    ...(pujas.length > 0 ? {
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: pujas.length,
        itemListElement: pujas.slice(0, 10).map((p, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Service',
            name: p.name,
            description: p.description,
            serviceType: 'Puja / religious ritual',
            url: `https://ayodhyadham.info/epuja/${p.id}`,
            areaServed: { '@type': 'City', name: 'Ayodhya' },
            ...(price(p) ? { offers: { '@type': 'Offer', price: price(p), priceCurrency: 'INR' } } : {}),
          },
        })),
      },
    } : {}),
  };

  return (
    <>
      <SEO
        title="Puja & Seva Booking"
        description="Sponsor a puja at Ayodhya's temples and receive prasad at home. Verified pandits, live video and a digital sankalp certificate."
        canonicalUrl="/epuja"
        keywords="online puja booking, e-puja Ram Mandir, virtual puja Ayodhya, puja from home, seva booking"
        structuredData={schema}
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Booking') }, { label: t('Puja Booking') }]} title={t('Puja & Seva Booking')} subtitle={t("Sponsor a puja at Ayodhya's temples and receive prasad at home")}>
        <ul className="usp-row" style={{ marginTop: 8 }}>
          {FEATURES.map((f) => (
            <li key={f.label}><IconTile size="sm" icon={f.icon} label={t(f.label)} sub={t(f.desc)} /></li>
          ))}
        </ul>
      </PageHero>

      <div className="container listing-wrap">
        <div className="listing__toolbar">
          <Tabs variant="pill" items={tabs} value={activeCategory} onChange={(v) => setParam('category', v)} ariaLabel={t('Puja category')} />
          <div className="listing__tools">
            <label className="search">
              <Icon name="search" size={16} />
              <span className="sr-only">{t('Search pujas')}</span>
              <input type="search" placeholder={t('Search pujas, deities, temples…')} value={search} onChange={(e) => onSearch(e.target.value)} />
            </label>
            <Button size="sm" variant="ghost" to="/pandits" icon={<Icon name="namaste" size={15} />} iconPosition="left">{t('Our pandits')}</Button>
          </div>
        </div>

        {loading ? (
          <div className="card-grid card-grid--wide">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={360} radius="var(--r-lg)" />)}</div>
        ) : error ? (
          <EmptyState icon="circle-alert" title={t('Could not load pujas')} body={t('The server is not reachable right now. Please try again.')} action={<Button variant="outline" onClick={() => setReload((k) => k + 1)}>{t('Retry')}</Button>} />
        ) : pujas.length === 0 ? (
          <EmptyState icon="diya" title={t('No pujas found')} body={t('Try a different search or category.')} action={<Button variant="outline" onClick={() => { onSearch(''); setParam('category', 'all'); }}>{t('Clear filters')}</Button>} />
        ) : (
          <>
            <div className="card-grid card-grid--wide">
              {pujas.map((puja, i) => (
                <article key={puja.id} className="card card--hover puja-card">
                  <div className="card__media card__media--wide">
                    <img src={puja.imageUrl || puja.image || PUJA_IMAGES[i % PUJA_IMAGES.length]} alt={puja.name} loading="lazy"
                      onError={(e) => { e.currentTarget.src = PUJA_IMAGES[i % PUJA_IMAGES.length]; }} />
                    {puja.category && <Badge variant="soft" className="puja-card__cat">{t(CATEGORY_LABELS[puja.category] || puja.category)}</Badge>}
                  </div>
                  <div className="card__body">
                    <h3 className="h-card">{puja.name}</h3>
                    {puja.description && <p className="place-card__sub">{puja.description}</p>}
                    <ul className="place-card__meta">
                      {puja.duration && <li><Icon name="clock" size={14} /><span>{puja.duration}</span></li>}
                      <li><Icon name="temple" size={14} /><span>{typeof puja.templeId === 'object' && puja.templeId?.name ? puja.templeId.name : t('Ayodhya Dham')}</span></li>
                    </ul>
                    <div className="amenities">
                      {[['video', t('Live video')], ['package', t('Prasad')], ['award', t('Certificate')]].map(([icon, label]) => (
                        <span key={label} className="badge badge--outline"><Icon name={icon} size={13} />{label}</span>
                      ))}
                    </div>
                    <div className="puja-card__foot">
                      <div className="price">
                        {price(puja) ? <>{t('from')} ₹{price(puja).toLocaleString('en-IN')}</> : <small>{t('Price on request')}</small>}
                      </div>
                      <Button size="sm" onClick={() => navigate(`/epuja/${puja.id}`)} icon={<Icon name="arrow-right" size={14} />}>{t('View & Book')}</Button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <span className="muted small">{t('Page {{page}} of {{total}}', { page: pagination.page, total: pagination.totalPages })}</span>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
